// src/components/markdown/Network.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { BaseCustomProps } from './types';

// Extended props type with new container sizing properties
interface NetworkProps extends BaseCustomProps {
  value?: string; // JSON string of nodes and edges
  containerHeight?: string | number; // Allow explicit height control
  fillContainer?: boolean; // Whether to fill the parent container
}

// Add vis.js types without importing the package
declare global {
  interface Window {
    vis?: any;
  }
}

// Helper function to get CSS variable value
const getCssVariableValue = (variableName: string, fallback: string): string => {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return fallback;
  }
  try {
    const correctedName = variableName.startsWith('--') ? variableName : `--${variableName}`;
    const value = getComputedStyle(document.documentElement).getPropertyValue(correctedName).trim();
    if (/^(\d{1,3})\s+(\d{1,3})%\s+(\d{1,3})%$/.test(value)) {
      return `hsl(${value.replace(/\s+/g, ', ')})`;
    }
    return value || fallback;
  } catch (e) {
    console.warn(`Could not read CSS variable ${variableName}:`, e);
    return fallback;
  }
};

// Helper function to convert RGB format to hex
const rgbToHex = (rgb: string): string => {
  const rgbMatch = rgb.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  }
  return rgb;
};

// Helper to process color values
const processColor = (color: string | undefined, fallback: string): string => {
  if (!color) return fallback;
  if (color.startsWith('rgb')) {
    return rgbToHex(color);
  }
  if (color.startsWith('var(')) {
     const varName = color.match(/--[a-zA-Z0-9-]+/)?.[0];
     if (varName) {
       return getCssVariableValue(varName, fallback);
     }
  }
  return color;
};

interface DebouncedFunction<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void; // Represents the function's call signature
  cancel(): void;                // Represents the cancel method
}

// Simple debounce function
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): DebouncedFunction<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  // This is the actual function that gets called after the delay
  const debounced = (...args: Parameters<T>) => {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };

  // Attach the cancel method directly to the debounced function object
  debounced.cancel = () => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}


const NetworkGraph: React.FC<NetworkProps> = ({ 
  value = '', 
  className, 
  containerHeight,
  fillContainer = true // Default to filling container
}) => {
  // --- Hooks ---
  const containerRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<any>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [isStabilizing, setIsStabilizing] = useState(false);
  const stabilizationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializationRef = useRef<boolean>(false);
  const [themeColors, setThemeColors] = useState({
    primary: '#FFA500', foreground: '#0A192F', background: '#FFFFFF', card: '#FFFFFF',
    border: '#E2E8F0', secondary: '#F1F5F9', teal: '#38B2AC',
  });

  // --- Effect for Mounting and Theme Colors ---
  useEffect(() => {
    let isMounted = true;
    setMounted(true);
    // Fetch theme colors
    setThemeColors({
        primary: getCssVariableValue('--primary', '#FFA500'),
        foreground: getCssVariableValue('--foreground', '#0A192F'),
        background: getCssVariableValue('--background', '#FFFFFF'),
        card: getCssVariableValue('--card', '#FFFFFF'),
        border: getCssVariableValue('--border', '#E2E8F0'),
        secondary: getCssVariableValue('--secondary', '#F1F5F9'),
        teal: getCssVariableValue('--teal', '#38B2AC'),
    });

    return () => {
      isMounted = false;
      // General cleanup on unmount
      if (stabilizationTimeoutRef.current) {
        clearTimeout(stabilizationTimeoutRef.current);
        stabilizationTimeoutRef.current = null;
      }
      if (resizeObserverRef.current) { resizeObserverRef.current.disconnect(); }
      if (styleRef.current && styleRef.current.parentNode) { try { styleRef.current.parentNode.removeChild(styleRef.current); } catch (e) {} }
      if (networkRef.current) { try { networkRef.current.destroy(); } catch (e) { console.warn("Error destroying network on unmount:", e)} }
      networkRef.current = null;
      styleRef.current = null;
      resizeObserverRef.current = null;
      initializationRef.current = false;
    };
  }, []);

  // --- Callback for loading Vis.js ---
  const ensureVisJsLoaded = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
        if (window.vis) { resolve(); return; }
        const cssId = 'vis-network-css', scriptId = 'vis-network-script';
        const cssHref = 'https://unpkg.com/vis-network@9.1.9/dist/dist/vis-network.min.css';
        const scriptSrc = 'https://unpkg.com/vis-network@9.1.9/dist/vis-network.min.js';
        
        if (!document.getElementById(cssId)) {
            const cssLink = document.createElement('link');
            cssLink.id = cssId; cssLink.rel = 'stylesheet'; cssLink.href = cssHref;
            document.head.appendChild(cssLink);
        }
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId; script.src = scriptSrc; script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load vis-network script'));
            document.head.appendChild(script);
        } else {
             let checks = 0;
             const interval = setInterval(() => {
                 if (window.vis) { clearInterval(interval); resolve(); }
                 else if (checks++ > 50) { clearInterval(interval); reject(new Error('Timeout waiting for vis-network')); }
             }, 100);
        }
    });
  }, []);

  // --- Helper: Calculate and set view to fit container ---
  const setViewToFitWidth = useCallback((networkInstance: any, containerElement: HTMLElement | null) => {
    if (!networkInstance?.body?.nodeIndices || !containerElement) return;

    try {
      const nodeIds = networkInstance.body.nodeIndices;
      if (nodeIds.length === 0) {
        networkInstance.moveTo({ scale: 1.0, animation: false });
        return;
      }

      const nodePositions = networkInstance.getPositions(nodeIds);
      const containerWidth = containerElement.clientWidth;
      const containerHeight = containerElement.clientHeight;
      
      if (containerWidth <= 0 || containerHeight <= 0) return;

      let minX: number | undefined, maxX: number | undefined;
      let minY: number | undefined, maxY: number | undefined;
      
      // Calculate graph bounds
      Object.keys(nodePositions).forEach((nodeId) => {
        const pos = nodePositions[nodeId];
        if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
          if (minX === undefined || pos.x < minX) minX = pos.x;
          if (maxX === undefined || pos.x > maxX) maxX = pos.x;
          if (minY === undefined || pos.y < minY) minY = pos.y;
          if (maxY === undefined || pos.y > maxY) maxY = pos.y;
        } else {
          console.warn(`Position data missing or invalid for node ID: ${nodeId}`);
        }
      });

      if (minX === undefined || maxX === undefined || minY === undefined || maxY === undefined) {
        console.warn("Could not determine graph bounds, falling back to fit().");
        networkInstance.fit({ animation: { duration: 400, easingFunction: 'linear' }});
        return;
      }

      // Calculate content dimensions and center
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;
      const contentCenterX = (minX + maxX) / 2;
      const contentCenterY = (minY + maxY) / 2;
      
      // Better padding (15% on each side)
      const paddingFactor = 1.3;
      
      // Calculate scales for width and height
      const scaleX = (containerWidth / (contentWidth * paddingFactor));
      const scaleY = (containerHeight / (contentHeight * paddingFactor));
      
      // Use the smaller scale to ensure everything fits
      let desiredScale = Math.min(scaleX, scaleY);
      
      // Reasonable scale limits
      desiredScale = Math.max(0.1, Math.min(desiredScale, 2.0));

      console.log(`Setting view: Center=(${contentCenterX.toFixed(2)}, ${contentCenterY.toFixed(2)}), Scale=${desiredScale.toFixed(2)}`);
      
      // Apply the view change with smooth animation
      networkInstance.moveTo({
        position: { x: contentCenterX, y: contentCenterY },
        scale: desiredScale,
        animation: { duration: 400, easingFunction: 'easeOutQuad' }
      });
    } catch (error) {
      console.error("Error calculating or setting view:", error);
      try { 
        networkInstance.fit({ animation: { duration: 400, easingFunction: 'linear' }}); 
      } catch (fitError) { 
        console.error("Fallback fit() also failed:", fitError); 
      }
    }
  }, []);

  // --- Callback for initializing the graph ---
  const initializeGraph = useCallback(async (graphData: any) => {
    if (!containerRef.current || !window.vis) { return null; }
    
    // Prevent concurrent initializations
    if (initializationRef.current) {
      console.log("Initialization already in progress, skipping...");
      return null;
    }
    initializationRef.current = true;
    
    setLoading(true); setError(null); setIsStabilizing(false);
    const container = containerRef.current;

    // Theme Defaults
    const defaultFontFamily = graphData.fontFamily || 'Euclid Circular A, arial, sans-serif';
    const defaultNodeColor = themeColors.secondary; const defaultNodeBorder = themeColors.border;
    const centralNodeColor = themeColors.primary; const defaultNodeFontColor = themeColors.foreground;
    const defaultEdgeColor = themeColors.teal; const defaultEdgeLabelColor = themeColors.foreground;
    const defaultEdgeLabelBackground = themeColors.card;
    const baseNodeFontSize = graphData.fontSize?.node || 24; 
    const baseEdgeFontSize = graphData.fontSize?.edge || 20; 

    // Destroy previous instance
    if (networkRef.current) { try { networkRef.current.destroy(); } catch (e) {} } networkRef.current = null;
    if (styleRef.current && styleRef.current.parentNode) { try { styleRef.current.parentNode.removeChild(styleRef.current); } catch (e) {} } styleRef.current = null;

    try {
        // Node processing
        const nodes = (graphData.nodes || []).map((node: any) => {
            const isCentral = node.id === "central" || (graphData.centralNode && node.id === graphData.centralNode);
            const nodeBgColor = processColor(node.color, isCentral ? centralNodeColor : defaultNodeColor);
            const nodeBorderColor = isCentral ? themeColors.primary : defaultNodeBorder;
            const nodeFontColor = processColor(node.font?.color, defaultNodeFontColor);
            const nodeFontSize = node.font?.size || (isCentral ? baseNodeFontSize + 2 : baseNodeFontSize);
            return {
                ...node, label: node.label || node.id,
                color: { background: nodeBgColor, border: nodeBorderColor, highlight: { background: nodeBgColor, border: themeColors.primary }, hover: { background: nodeBgColor, border: themeColors.primary } },
                font: { color: nodeFontColor, size: nodeFontSize, face: node.font?.face || defaultFontFamily, bold: isCentral || node.font?.bold || false, multi: node.font?.multi || 'html', align: node.font?.align || 'center', vadjust: node.font?.vadjust || 0, },
                borderWidth: isCentral ? 2 : 1, borderWidthSelected: 2, shape: node.shape || 'box',
                shapeProperties: { borderRadius: node.shapeProperties?.borderRadius ?? 6 },
                margin: node.margin || { top: 10, right: 15, bottom: 10, left: 15 },
            };
        });
        // Edge processing
        const edges = (graphData.edges || []).map((edge: any) => {
            const edgeLineColor = processColor(edge.color, defaultEdgeColor);
            const edgeFontColor = processColor(edge.font?.color, defaultEdgeLabelColor);
            const edgeFontBackground = processColor(edge.font?.background, defaultEdgeLabelBackground);
            const edgeFontSize = edge.font?.size || baseEdgeFontSize;
            return {
                ...edge, from: edge.source, to: edge.target,
                arrows: edge.directed !== false ? { to: { enabled: true, scaleFactor: 0.8, type: 'arrow' } } : undefined,
                color: { color: edgeLineColor, highlight: themeColors.primary, hover: themeColors.primary, opacity: edge.opacity ?? 0.9 },
                width: edge.width || 1.5, widthConstraint: edge.widthConstraint, label: edge.label,
                font: edge.label ? { size: edgeFontSize, color: edgeFontColor, face: edge.font?.face || defaultFontFamily, background: edgeFontBackground, strokeWidth: edge.font?.strokeWidth ?? 0, align: edge.font?.align || 'middle', vadjust: edge.font?.vadjust || -5, } : undefined,
                smooth: edge.smooth ?? { enabled: true, type: 'dynamic', roundness: 0.5 },
                length: edge.length
            };
        });

        // --- Vis.js Options with Stability Fixes ---
        const isHierarchical = graphData.layout?.hierarchical === true || (typeof graphData.layout?.hierarchical === 'object' && graphData.layout.hierarchical.enabled !== false);
        const physicsEnabled = isHierarchical ? (graphData.physics?.enabled === true) : (graphData.physics?.enabled ?? true);
        const defaultSpringLength = 150; // Default length for node spacing

        const options = {
            nodes: {
                font: { color: defaultNodeFontColor, size: baseNodeFontSize, face: defaultFontFamily, multi: 'html', },
                borderWidth: 1, shape: 'box', shapeProperties: { borderRadius: 6 }, margin: { top: 10, right: 15, bottom: 10, left: 15 },
                widthConstraint: { minimum: 80 }, heightConstraint: { minimum: 40, valign: 'middle' },
                scaling: { label: { enabled: true, min: baseNodeFontSize, max: baseNodeFontSize * 1.5 } }
            },
            edges: {
                width: 1.5, color: { color: defaultEdgeColor, opacity: 0.9 },
                font: { color: defaultEdgeLabelColor, size: baseEdgeFontSize, face: defaultFontFamily, background: defaultEdgeLabelBackground, strokeWidth: 0, align: 'middle', },
                smooth: { enabled: true, type: 'dynamic', roundness: 0.5 },
                arrows: { to: { enabled: true, scaleFactor: 0.8, type: 'arrow' } },
                hoverWidth: 0.5, selectionWidth: 1
            },
            physics: {
                enabled: physicsEnabled,
                solver: graphData.physics?.solver || 'forceAtlas2Based',
                forceAtlas2Based: {
                    gravitationalConstant: graphData.physics?.forceAtlas2Based?.gravitationalConstant ?? -1000,
                    centralGravity: graphData.physics?.forceAtlas2Based?.centralGravity ?? 0.01,
                    springLength: graphData.physics?.forceAtlas2Based?.springLength ?? defaultSpringLength,
                    springConstant: graphData.physics?.forceAtlas2Based?.springConstant ?? 0.06,
                    damping: graphData.physics?.forceAtlas2Based?.damping ?? 0.85, // High damping
                    avoidOverlap: graphData.physics?.forceAtlas2Based?.avoidOverlap ?? 1 // Keep avoidOverlap=1
                },
                barnesHut: { damping: 0.85, ...(graphData.physics?.barnesHut || {}) },
                repulsion: { damping: 0.85, ...(graphData.physics?.repulsion || {}) },
                stabilization: {
                    enabled: true,
                    iterations: graphData.physics?.stabilization?.iterations ?? 500, // Reduced from 1000 to prevent infinite loops
                    updateInterval: 50, // Increased from 25 to reduce update frequency
                    fit: true,
                    onlyDynamicEdges: false
                },
                maxVelocity: 50, // Limit maximum velocity to prevent runaway physics
                minVelocity: 0.1, // Minimum velocity threshold
                timestep: 0.5 // Stable timestep
            },
            interaction: {
                hover: graphData.interaction?.hover ?? true, tooltipDelay: graphData.interaction?.tooltipDelay ?? 300,
                navigationButtons: graphData.interaction?.navigationButtons ?? false, keyboard: graphData.interaction?.keyboard ?? false,
                zoomView: graphData.interaction?.zoomView ?? true, dragView: graphData.interaction?.dragView ?? true,
                dragNodes: graphData.interaction?.dragNodes ?? false, selectable: graphData.interaction?.selectable ?? true,
                selectConnectedEdges: graphData.interaction?.selectConnectedEdges ?? true, multiselect: graphData.interaction?.multiselect ?? false,
            },
            layout: {
                 randomSeed: graphData.layout?.randomSeed, improvedLayout: graphData.layout?.improvedLayout ?? true,
                 hierarchical: isHierarchical ? { enabled: true, direction: graphData.layout?.hierarchical?.direction || 'LR', sortMethod: graphData.layout?.hierarchical?.sortMethod || 'directed', levelSeparation: graphData.layout?.hierarchical?.levelSeparation || 250, nodeSpacing: graphData.layout?.hierarchical?.nodeSpacing || 150, } : false,
            },
            configure: false
        };

        // Container Sizing - UPDATED
        let height: string | number;
        
        if (containerHeight) {
          // Use explicitly provided height if available
          height = containerHeight;
        } else if (graphData.height) {
          // Use graph data height if specified
          height = graphData.height;
        } else if (fillContainer) {
          // Fill parent container (70vh as fallback)
          height = container.parentElement?.clientHeight || '70vh';
        } else {
          // Reasonable default that's much larger than before
          height = 600;
        }

        // Apply the height
        container.style.height = typeof height === 'number' ? `${height}px` : height;
        
        // Make width fully responsive
        container.style.width = '100%';
        container.style.maxWidth = '100%';

        // Network Creation
        const networkData = { nodes: new window.vis.DataSet(nodes), edges: new window.vis.DataSet(edges) };
        const network = new window.vis.Network(container, networkData, options);
        networkRef.current = network;

        // Custom CSS
        const style = document.createElement('style');
        style.innerHTML = `
          .vis-network .vis-node > div { color: ${defaultNodeFontColor} !important; }
          .vis-network .vis-edge .vis-label { padding: 2px 4px; border-radius: 3px; pointer-events: none; }`;
        document.head.appendChild(style);
        styleRef.current = style;

        // --- Stabilization Listeners ---
        let stabilizationStarted = false;
        
        network.on('startStabilizing', () => {
            if (!stabilizationStarted) {
                console.log("Stabilization started...");
                stabilizationStarted = true;
                setIsStabilizing(true);
            }
        });
        
        network.on('stabilizationProgress', (params: { iterations: number, total: number }) => {
            const percentage = Math.round(params.iterations / params.total * 100);
            if (percentage % 10 === 0) { // Only log every 10%
                console.log(`Stabilization progress: ${params.iterations}/${params.total} (${percentage}%)`);
            }
        });
        
        network.on('stabilized', (params: { iterations: number }) => {
            console.log(`Physics stabilized after ${params.iterations} iterations.`);
            setIsStabilizing(false);
        });
        
        network.once('stabilizationIterationsDone', () => {
            console.log("Stabilization iterations done. Setting initial view...");
            setIsStabilizing(false);
            stabilizationStarted = false;
            
            // Clear any existing timeout
            if (stabilizationTimeoutRef.current) {
                clearTimeout(stabilizationTimeoutRef.current);
                stabilizationTimeoutRef.current = null;
            }
            
            setTimeout(() => {
                if (networkRef.current && containerRef.current) {
                    setViewToFitWidth(networkRef.current, containerRef.current);
                }
                setLoading(false);
                initializationRef.current = false;
            }, 100);
        });

        // Fallback timeout with stabilization stop
        stabilizationTimeoutRef.current = setTimeout(() => {
            console.warn("Network stabilization timeout reached. Stopping physics...");
            if (networkRef.current) {
                // Force stop physics to prevent infinite loop
                networkRef.current.stopSimulation();
                networkRef.current.physics.stopSimulation();
                
                // Set view even if not stabilized
                setViewToFitWidth(networkRef.current, containerRef.current);
            }
            setLoading(false);
            setIsStabilizing(false);
            initializationRef.current = false;
        }, 5000); // Reduced to 5 seconds

        // Cleanup for this specific initialization
        return () => {
           if (stabilizationTimeoutRef.current) {
               clearTimeout(stabilizationTimeoutRef.current);
               stabilizationTimeoutRef.current = null;
           }
           initializationRef.current = false;
           // Style tag cleanup is handled by main effect cleanup
        };

    } catch (err) {
        console.error('Error initializing network graph:', err);
        setError(err instanceof Error ? err.message : 'Unknown error loading graph');
        setLoading(false);
        return null;
    }
  }, [themeColors, setViewToFitWidth, containerHeight, fillContainer]); // Removed loading to prevent re-initialization

  // --- Effect for Initializing the Graph ---
  useEffect(() => {
    let isMounted = true;
    let cleanupGraphInit: (() => void) | null = null;

    const loadAndInitActual = async () => {
      if (!isMounted || !value || !mounted) return;
      
      // Destroy existing network before creating new one
      if (networkRef.current) {
        try {
          networkRef.current.destroy();
          networkRef.current = null;
        } catch (e) {
          console.warn("Error destroying existing network:", e);
        }
      }
      
      setLoading(true); setError(null);
      try {
        await ensureVisJsLoaded();
        if (!isMounted) return;
        let parsedData; 
        try { parsedData = JSON.parse(value); } 
        catch (err) { 
          setError("Invalid JSON data for graph"); 
          setLoading(false); 
          initializationRef.current = false;
          return; 
        }
        cleanupGraphInit = await initializeGraph(parsedData);
      } catch (err) { 
        setError(err instanceof Error ? err.message : 'Unknown error loading graph'); 
        setLoading(false); 
        initializationRef.current = false;
      }
    };

    if (mounted && value) { loadAndInitActual(); }
    else if (!value && mounted) {
        setError(null); setLoading(false);
        if (networkRef.current) { try { networkRef.current.destroy(); } catch (e) {} networkRef.current = null; }
        if (styleRef.current && styleRef.current.parentNode) { try { styleRef.current.parentNode.removeChild(styleRef.current); } catch (e) {} styleRef.current = null; }
    }

    return () => { isMounted = false; if (cleanupGraphInit) { cleanupGraphInit(); } };
  }, [value, mounted, ensureVisJsLoaded, initializeGraph]);

  // --- Effect for Resize Observer - UPDATED ---
  useEffect(() => {
    if (!mounted) return;
    
    // More responsive resize handler with shorter debounce
    const debouncedViewSetter = debounce(() => {
      if (networkRef.current && containerRef.current) {
        // Force a relayout before setting the view
        networkRef.current.redraw();
        setViewToFitWidth(networkRef.current, containerRef.current);
      }
    }, 100); // Reduced from 150ms to 100ms for more responsive resizing
    
    let observer: ResizeObserver | null = null;
    
    if (containerRef.current) {
      observer = new ResizeObserver((entries) => {
        // Check if size actually changed significantly
        const entry = entries[0];
        if (entry && (entry.contentRect.width > 0 || entry.contentRect.height > 0)) {
          debouncedViewSetter();
        }
      });
      
      observer.observe(containerRef.current);
      resizeObserverRef.current = observer;
      
      // Also listen for window resize events to handle parent container changes
      window.addEventListener('resize', debouncedViewSetter);
    } else { 
      console.warn("Container ref not available for ResizeObserver"); 
    }
    
    return () => {
      debouncedViewSetter.cancel?.();
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      window.removeEventListener('resize', debouncedViewSetter);
    };
  }, [mounted, setViewToFitWidth]); 


  // --- Conditional Rendering & Final Render - UPDATED ---
  if (!mounted) { 
    return (
      <div className={cn("w-full mx-auto h-[50vh] min-h-[400px] bg-card border rounded-lg flex items-center justify-center text-muted-foreground", className)}>
        Loading Graph...
      </div>
    ); 
  }

  if (!value) { 
    return (
      <Card className={cn("w-full mx-auto", className)}>
        <CardContent className="p-4 text-center text-muted-foreground">
          No network data provided.
        </CardContent>
      </Card>
    ); 
  }

  let title: string | null = null; 
  try { 
    const data = JSON.parse(value); 
    title = data.title || null; 
  } catch (e) { 
    if (!error) setError("Invalid JSON format for title parsing."); 
  }

  // Calculate default height based on container filling or fixed height
  const defaultContainerStyle = fillContainer 
    ? { height: containerHeight || '70vh', minHeight: '400px' } 
    : { height: containerHeight || '600px', minHeight: '300px' };

  return (
    <Card className={cn("w-full mx-auto overflow-hidden relative", className)}>
      {title && (
        <CardHeader className="p-3 sm:p-4 border-b">
          <CardTitle className="text-center text-lg sm:text-xl font-semibold" 
            style={{ fontFamily: getCssVariableValue('--font-heading', 'Crimson Pro, serif') }}>
            {title}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="p-0 relative">
        {(loading || isStabilizing) && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
            <div className="flex flex-col items-center gap-2">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
              {isStabilizing && (
                <div className="text-sm text-muted-foreground">Stabilizing network...</div>
              )}
            </div>
          </div>
        )}
        {error && !loading && (
          <div className="p-4 text-center text-red-600 bg-red-50 z-10 relative border-b">
            <p className="font-semibold">Error:</p>
            <p className="text-sm">{error}</p>
          </div>
        )}
        <div 
          ref={containerRef} 
          className={cn(
            "vis-network-container w-full",
            "bg-card",
            error ? "opacity-30" : "opacity-100",
            "transition-opacity duration-300"
          )} 
          style={defaultContainerStyle}
        >
          {/* Vis.js renders here */}
        </div>
      </CardContent>
    </Card>
  );
};

export default NetworkGraph;